import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import api from "../lib/api";

// Prompt 15 PART C — persistent red banner surfaced on every page when
// any GL connection has gone expired/revoked/offline. Polls once on
// mount and every 60s thereafter, falls silent when the attention count
// drops to zero. The banner is **informational only** — clicking it
// routes to the Connections page where the user does the real repair.
export default function AttentionBar() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/alerts/attention");
        if (alive) setData(r.data);
      } catch (_e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data || dismissed) return null;
  const needs = data.needs_reconnect || [];
  if (needs.length === 0) return null;

  const first = needs[0];
  const label = first?.name || first?.erp || "A GL";
  const extra = needs.length > 1 ? ` and ${needs.length - 1} other${needs.length === 2 ? "" : "s"}` : "";

  return (
    <div
      role="alert"
      className="border-b border-red-700 bg-red-600 text-white"
      data-testid="attention-bar"
    >
      <div className="flex items-center gap-3 px-6 py-2 text-sm">
        <AlertTriangle size={16} className="shrink-0" />
        <div className="flex-1 truncate" data-testid="attention-bar-text">
          <strong className="font-semibold">{label}</strong>
          {" "}connection {needs.length > 1 ? "connections" : "connection"} needs
          attention{extra}. {first?.reason ? <span className="opacity-90">— {first.reason}</span> : null}
        </div>
        <button
          type="button"
          onClick={() => navigate("/connections")}
          className="uppercase tracking-widest text-xs underline whitespace-nowrap"
          data-testid="attention-bar-cta"
        >
          Open connections
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="opacity-80 hover:opacity-100"
          data-testid="attention-bar-dismiss"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
